import { Component, EventEmitter, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit, OnDestroy, HostListener, ViewEncapsulation, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Observable, Subject, Subscription } from 'rxjs';
import { FootNotesService } from './foot-notes.service';
import { CopyNoteDialogComponent, CopyNoteDialogData } from './copy-note-dialog/copy-note-dialog.component';
import { ConfirmationModalComponent } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';
import {
  DecoupledEditor,
  Essentials,
  Paragraph,
  Heading,
  FontSize,
  FontFamily,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Alignment,
  List,
  Indent,
  IndentBlock,
  Table,
  TableToolbar,
} from 'ckeditor5';

@Component({
  selector: 'gtw-foot-notes',
  templateUrl: './foot-notes.component.html',
  styleUrls: ['./foot-notes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class FootNotesComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly overlay = inject(Overlay);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  @ViewChild('editorToolbar') editorToolbar!: ElementRef;

  private _editorEditable!: ElementRef;
  @ViewChild('editorEditable') set editorEditable(el: ElementRef) {
    this._editorEditable = el;
    if (el && !this.editorInitialized && this.initDone) {
      this.initEditor();
    }
  }
  get editorEditable(): ElementRef {
    return this._editorEditable;
  }

  private readonly subs = new Subscription();

  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};

  selectedFilterType = 'P';
  hoList: any[] = [];
  formScheduleList: any[] = [];
  scheduleList: any[] = [];
  partners: any[] = [];
  selectedHO: any = null;
  selectedForm: any;
  selectedSchedule: any;
  selectedPartner: any;
  hideSchedules = true;
  hidePartners = true;
  hoLoading = false;

  hoSearchTerm = '';
  hoDropdownOpen = false;

  compareHO = (a: any, b: any) => a?.value === b?.value;

  footNotes: any[] = [];
  footNotesLoading = false;
  footNotesLoaded = false;
  activeNote: any = {};
  activeNoteCopy: any = {};

  saving = false;
  deletingNote = false;

  private pendingConfirm?: Observable<boolean>;

  editor: any;
  private editorInitialized = false;
  private initDone = false;

  private pdfEngineUrl = '';

  constructor(
    private footNotesService: FootNotesService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(_event: Event): void {
    const event = _event as BeforeUnloadEvent;
    if (this.activeNote?.note_name && this.activeNoteChanged()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  ngOnInit(): void {
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwWorkspace.screens.subscribe(() => {
        if (this.activeNote?.note_name && this.activeNoteChanged()) {
          this.showUnsavedDialog();
        }
      })
    );
  }

  ngAfterViewInit(): void {
    if (this.pdfEngineUrl && !this.editorInitialized) {
      this.initEditor();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.destroyEditor();
  }

  private destroyEditor(): void {
    if (this.editor) {
      this.editor.destroy().catch((err: any) => console.error(err));
      this.editor = null;
    }
    this.editorInitialized = false;
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.GENERAL_CONFIG || !this.globalService) {
        return;
      }
      this.initDone = true;
      this.pdfEngineUrl = this.GENERAL_CONFIG.pdfEngine || this.GENERAL_CONFIG.pdf_engine_api_url || '';
      this.loadDropdowns();
      if (!this.editorInitialized && this.editorEditable) {
        this.initEditor();
      }
    } catch (e) {
      console.error('Error in FootNotes init:', e);
    }
  }

  private initEditor(): void {
    if (this.editorInitialized || !this.editorEditable) return;

    this.editorInitialized = true;
    const editableEl = this.editorEditable.nativeElement;

    DecoupledEditor.create(editableEl, {
      plugins: [
        Essentials, Paragraph, Heading, FontSize, FontFamily,
        Bold, Italic, Underline, Strikethrough, Alignment,
        List, Indent, IndentBlock, Table, TableToolbar,
      ],
      fontFamily: {
        options: [
          'Arial, Helvetica, sans-serif',
          'Courier New, Courier, monospace',
          'Georgia, serif',
          'Lucida Sans Unicode, Lucida Grande, sans-serif',
          'Tahoma, Geneva, sans-serif',
          'Times New Roman, Times, serif',
          'Trebuchet MS, Helvetica, sans-serif',
          'Verdana, Geneva, sans-serif',
        ],
      },
      fontSize: {
        options: ['tiny', 'small', 'default', 'big', 'huge'],
      },
      toolbar: {
        items: [
          'heading', '|', 'fontSize', 'fontFamily', '|',
          'bold', 'italic', 'underline', 'strikethrough', '|',
          'alignment', '|', 'numberedList', 'bulletedList', '|',
          'indent', 'outdent', '|',
          'insertTable', '|',
          'undo', 'redo'
        ],
      },
      table: {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
      }
    }).then((createdEditor: any) => {
      if (this.editorToolbar) {
        this.editorToolbar.nativeElement.appendChild(createdEditor.ui.view.toolbar.element);
        this.addPageBreakButton();
      }
      createdEditor.execute('fontFamily', { value: 'Verdana' });
      this.editor = createdEditor;

      const firstNote = _.minBy(this.footNotes, 'order_id');
      if (firstNote) {
        this.activateNote(firstNote);
      }
      this.cdr.detectChanges();
    }).catch((error: any) => {
      console.error('CKEditor initialization error:', error);
    });
  }

  private addPageBreakButton(): void {
    if (!this.editorToolbar) return;

    const toolbar = this.editorToolbar.nativeElement.querySelector('.ck-toolbar__items');
    if (!toolbar) return;

    const pageBreakBtn = document.createElement('button');
    pageBreakBtn.type = 'button';
    pageBreakBtn.className = 'ck ck-button page-break-btn';
    pageBreakBtn.title = 'Page break';
    pageBreakBtn.innerHTML = `
      <svg class="ck ck-icon ck-button__icon" viewBox="0 0 20 20">
        <path d="M3.598.687h1.5v5h-1.5zm14.5 0h1.5v5h-1.5z"></path>
        <path d="M19.598 4.187v1.5h-16v-1.5zm-16 14.569h1.5v-5h-1.5zm14.5 0h1.5v-5h-1.5z"></path>
        <path d="M19.598 15.256v-1.5h-16v1.5zM5.081 9h6v2h-6zm8 0h6v2h-6zm-9.483 1L0 12.5v-5z"></path>
      </svg>
    `;
    pageBreakBtn.addEventListener('click', () => this.insertPageBreak());

    // The insertTable control is a dropdown, so its inner `.ck-button` is NOT a direct
    // child of `.ck-toolbar__items`. We must locate the toolbar ITEM (a direct child)
    // that hosts the table control, otherwise `insertBefore` throws NotFoundError because
    // the reference node isn't a child of the toolbar items container.
    const items = Array.from(toolbar.children) as Element[];
    const mentionsTable = (el: Element | null): boolean => {
      if (!el) return false;
      const tip = el.getAttribute('data-cke-tooltip-text') || el.getAttribute('aria-label') || '';
      return tip.toLowerCase().includes('table');
    };
    const tableItem = items.find(
      (item) => mentionsTable(item) || mentionsTable(item.querySelector('.ck-button'))
    );

    if (tableItem) {
      // nextSibling of a direct child is itself a child (or null -> append): always valid.
      toolbar.insertBefore(pageBreakBtn, tableItem.nextSibling);
    } else {
      // Fallback: insert before the last separator (before undo/redo)
      const separators = toolbar.querySelectorAll('.ck-toolbar__separator');
      if (separators.length > 0) {
        const lastSeparator = separators[separators.length - 1];
        toolbar.insertBefore(pageBreakBtn, lastSeparator);
      } else {
        toolbar.appendChild(pageBreakBtn);
      }
    }
  }

  insertPageBreak(): void {
    if (!this.editor) return;

    const currentData = this.editor.getData();
    
    // Page break with empty paragraph after for cursor placement
    const pageBreakHtml = '<p style="text-align:center;border-top:2px dashed #999;padding-top:8px;margin:25px 0;color:#666;font-size:11px;letter-spacing:1px;">-------------------------------------------------------- Page Break --------------------------------------------------------</p><p>&nbsp;</p>';
    
    this.editor.setData(currentData + pageBreakHtml);
    
    // Move cursor to the end (the new empty paragraph)
    this.editor.model.change((writer: any) => {
      const root = this.editor.model.document.getRoot();
      const lastChild = root.getChild(root.childCount - 1);
      if (lastChild) {
        writer.setSelection(lastChild, 'end');
      }
    });
    
    // Focus the editor
    this.editor.editing.view.focus();
  }

  loadDropdowns() {
    this.hoLoading = true;
    const taxYear = this.globalService?.tax_year || this.filterParams.tax_year;
    const scenario = this.globalService?.scenario || this.filterParams.scenario;

    this.footNotesService.getViewForDropdown(
      this.pdfEngineUrl, taxYear, scenario, 'P', 'D', 'HO'
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        const list = response.viewList;
        if (list && list.length > 0) {
          this.hoList = list;
        }
        this.hoLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.hoLoading = false;
        this.cdr.detectChanges();
      }
    );

    const filingType = this.selectedFilterType === 'P' ? 'PSHIP' : 'CORP';
    this.footNotesService.getForms(this.pdfEngineUrl, taxYear, filingType, 'D').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === '1') {
          this.formScheduleList = response.forms;
          this.selectedForm = this.formScheduleList.length > 0 ? this.formScheduleList[0] : null;
          this.changeForm();
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading forms:', error);
      }
    );
  }

  switchFilters(filterType: string) {
    this.selectedFilterType = filterType;
    this.cdr.detectChanges();
  }

  changeHO() {
    if (!this.selectedHO) return;
    const pieces = this.selectedHO.value.split('/');
    const rptPrd = this.selectedHO.name.substring(
      this.selectedHO.name.lastIndexOf('/') + 1,
      this.selectedHO.name.lastIndexOf(')')
    );
    const leid = pieces[0];
    const cdrNo = pieces[1];
    this.selectedPartner = undefined;

    const taxYear = this.globalService?.tax_year || this.filterParams.tax_year;
    const scenario = this.globalService?.scenario || this.filterParams.scenario;

    this.footNotesService.getPartners(this.pdfEngineUrl, taxYear, scenario, leid, cdrNo, rptPrd).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.partnersList) {
          this.partners = response.partnersList;
          if (this.partners.length === 0 || (this.selectedForm && !this.selectedForm.form.toLowerCase().includes('sch'))) {
            this.hidePartners = true;
            this.selectedPartner = { leid: null };
          } else {
            this.hidePartners = false;
            this.selectedPartner = this.partners[0];
          }
        }
        this.cdr.detectChanges();
      }
    );
  }

  changeForm() {
    if (!this.selectedForm) return;
    this.scheduleList = this.selectedForm.schedules || [];
    if (this.scheduleList.length > 0) {
      this.selectedSchedule = this.scheduleList[0];
    }
    this.hideSchedules = !this.scheduleList.length || this.scheduleList[0] === null;

    if (this.selectedForm && !this.selectedForm.form.toLowerCase().includes('sch')) {
      this.hidePartners = true;
      this.selectedPartner = { leid: null };
    } else if (this.partners.length > 0) {
      this.hidePartners = false;
      this.selectedPartner = this.partners[0];
    }
    this.cdr.detectChanges();
  }

  onFilterClick() {
    if (this.activeNote && this.activeNote.note_name && this.activeNoteChanged()) {
      this.showConfirmDialog(
        'Unsaved Footnote',
        'You have unsaved changes on the footnote, are you sure you want to discard the changes and continue?'
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(proceed => {
        if (proceed) {
          this.loadFootNotes();
        }
      });
    } else if (this.selectedHO && this.selectedForm && this.selectedPartner !== undefined) {
      this.loadFootNotes();
    }
  }

  loadFootNotes() {
    this.destroyEditor();
    this.footNotesLoading = true;
    this.footNotesLoaded = false;
    this.activeNote = null;
    this.cdr.detectChanges();

    const taxYear = this.globalService?.tax_year || this.filterParams.tax_year;
    const scenario = this.globalService?.scenario || this.filterParams.scenario;
    const pieces = this.selectedHO.value.split('/');
    const rptPrd = this.selectedHO.name.substring(
      this.selectedHO.name.lastIndexOf('/') + 1,
      this.selectedHO.name.lastIndexOf(')')
    );

    this.footNotesService.getFootNotes(
      this.pdfEngineUrl, taxYear, scenario, 250,
      pieces[0], pieces[1], rptPrd,
      this.selectedPartner.leid, this.selectedForm.form, this.selectedSchedule
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (footNotes: any) => {
        this.footNotesLoading = false;
        this.footNotesLoaded = true;
        if (!footNotes) {
          this.unexpectedError();
          this.cdr.detectChanges();
          return;
        }
        this.footNotes = footNotes;
        this.cdr.detectChanges();
        setTimeout(() => this.initEditor(), 0);
      },
      (err: any) => {
        this.footNotesLoading = false;
        this.unexpectedError();
        this.cdr.detectChanges();
      }
    );
  }

  noteSelected(note: any) {
    // For unsaved notes (note_id is null), just activate directly without API call
    if (note.note_id === null) {
      this.activateNote(note);
      return;
    }
    note.contentLoading = true;
    this.cdr.detectChanges();

    this.footNotesService.getFootNote(this.pdfEngineUrl, note.note_id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (fullNote: any) => {
        note.contentLoading = false;
        if (fullNote && fullNote.html_data) {
          note.html_data = fullNote.html_data;
          this.activateNote(note);
        } else {
          this.unexpectedError();
        }
        this.cdr.detectChanges();
      },
      (e: any) => {
        note.contentLoading = false;
        this.unexpectedError();
        this.cdr.detectChanges();
      }
    );
  }

  activateNote(note: any) {
    // Save current editor content to the previous active note before switching
    if (this.activeNote && this.editor) {
      this.activeNote.html_data = this.editor.getData();
    }
    this.footNotes.forEach(n => n.active = false);
    if (this.editor) {
      this.editor.setData(note.html_data || '');
    }
    note.active = true;
    this.activeNote = note;
    this.activeNoteCopy = _.cloneDeep(this.activeNote);
    this.cdr.detectChanges();
  }

  addNote() {
    const newOrderId = (_.max(_.map(this.footNotes, 'order_id')) || 0) + 1;
    const newNote: any = {
      note_id: null,
      note_name: 'Untitled',
      html_data: '',
      order_id: newOrderId,
      input: true,
    };
    this.footNotes.push(newNote);
    this.activateNote(newNote);
  }

  saveContent() {
    if (!this.activeNote) return;

    if (this.activeNote.note_name === 'Untitled') {
      this._snackBar.open('Please name your footnote by double clicking on the title!', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.saving = true;
    this.cdr.detectChanges();

    const taxYear = this.activeNote.tax_year || this.globalService?.tax_year || this.filterParams.tax_year;
    const scenario = this.activeNote.scenario || this.globalService?.scenario || this.filterParams.scenario;
    const editorData = this.editor ? this.editor.getData() : (this.activeNote.html_data || '');

    this.footNotesService.saveFootNote(
      this.pdfEngineUrl,
      this.activeNote.note_id,
      this.activeNote.note_name,
      taxYear,
      scenario,
      this.selectedHO?.me_code || '',
      this.activeNote.leid || this.selectedHO.value.split('/')[0],
      this.activeNote.cdr_no || this.selectedHO.value.split('/')[1],
      this.activeNote.reporting_period || this.selectedHO.name.substring(
        this.selectedHO.name.lastIndexOf('/') + 1,
        this.selectedHO.name.lastIndexOf(')')
      ),
      this.activeNote.form_sub || this.selectedPartner.leid,
      this.activeNote.form_name || this.selectedForm.form,
      this.activeNote.schedule || this.selectedSchedule,
      editorData,
      'N'
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.saving = false;
        if (response.callSuccess === '1') {
          this._snackBar.open('Saved Successfully!', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.activeNote.note_id = response.footNote.note_id;
          this.activeNote.html_data = response.footNote.html_data;
          this.activeNoteCopy = _.cloneDeep(this.activeNote);
        } else {
          this._snackBar.open('Save Failed!', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.saving = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  deleteNote() {
    if (!this.activeNote || this.deletingNote) return;

    this.showConfirmDialog(
      'Delete Footnote',
      'Are you sure you want to delete this footnote?',
      'fa fa-sign-out txt-color-orangeDark'
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(proceed => {
      if (!proceed) return;
      this.performDelete();
    });
  }

  private performDelete() {
    this.deletingNote = true;
    this.cdr.detectChanges();

    this.footNotesService.deleteNote(this.pdfEngineUrl, this.activeNote.note_id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === '1') {
          this._snackBar.open('Footnote deleted!', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.loadFootNotesAfterDelete();
        } else {
          this.unexpectedError();
          this.deletingNote = false;
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.unexpectedError();
        this.deletingNote = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadFootNotesAfterDelete() {
    const taxYear = this.globalService?.tax_year || this.filterParams.tax_year;
    const scenario = this.globalService?.scenario || this.filterParams.scenario;
    const pieces = this.selectedHO.value.split('/');
    const rptPrd = this.selectedHO.name.substring(
      this.selectedHO.name.lastIndexOf('/') + 1,
      this.selectedHO.name.lastIndexOf(')')
    );

    this.footNotesService.getFootNotes(
      this.pdfEngineUrl, taxYear, scenario, 250,
      pieces[0], pieces[1], rptPrd,
      this.selectedPartner.leid, this.selectedForm.form, this.selectedSchedule
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (footNotes: any) => {
        this.deletingNote = false;
        if (!footNotes) {
          this.unexpectedError();
          this.cdr.detectChanges();
          return;
        }
        this.footNotes = footNotes;
        const firstNote = _.minBy(this.footNotes, 'order_id');
        if (firstNote) {
          this.activateNote(firstNote);
        }
        this.cdr.detectChanges();
      },
      (err: any) => {
        this.deletingNote = false;
        this.unexpectedError();
        this.cdr.detectChanges();
      }
    );
  }

  exportPdf() {
    if (this.deletingNote || !this.editor) return;
    const editorHtml = this.editor.getData();
    const html = this.wrapWithPageBreakStyles(editorHtml);
    this.footNotesService.exportPdf(this.pdfEngineUrl, html).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private wrapWithPageBreakStyles(content: string): string {
    // Convert page break elements to the format expected by PDF engine
    // Handle both div and p versions (CKEditor may convert div to p)
    let convertedContent = content;
    
    // Match div.page-break
    convertedContent = convertedContent.replace(
      /<div class="page-break"[^>]*>[\s\S]*?<\/div>/gi,
      '<div class="page-break" style="page-break-after:always;"><span style="display:none;">&nbsp;</span></div><p>&nbsp;</p>'
    );
    
    // Match p containing "Page Break" text (CKEditor converted version)
    convertedContent = convertedContent.replace(
      /<p[^>]*>[\s]*[—–-]+[\s]*Page Break[\s]*[—–-]+[\s]*<\/p>/gi,
      '<div class="page-break" style="page-break-after:always;"><span style="display:none;">&nbsp;</span></div><p>&nbsp;</p>'
    );
    
    return convertedContent;
  }

  cloneNote() {
    if (this.deletingNote || !this.activeNote) return;

    const taxYear = this.globalService?.tax_year || this.filterParams.tax_year;
    const scenario = this.globalService?.scenario || this.filterParams.scenario;

    const dialogData: CopyNoteDialogData = {
      hoList: this.hoList,
      formList: this.formScheduleList,
      scheduleList: this.scheduleList,
      partners: this.partners,
      defaultSelections: {
        selectedFilterType: this.selectedFilterType,
        selectedHO: this.selectedHO,
        selectedForm: this.selectedForm,
        selectedSchedule: this.selectedSchedule,
        selectedPartner: this.selectedPartner,
        hideSchedules: this.hideSchedules,
        hidePartners: this.hidePartners,
      },
      activeNote: this.activeNote,
      pdfEngineUrl: this.pdfEngineUrl,
      taxYear,
      scenario,
    };

    this.dialog.open(CopyNoteDialogComponent, {
      width: '460px',
      data: dialogData,
      panelClass: 'copy-note-dialog-panel',
    });
  }

  showMessage(): boolean {
    return this.footNotesLoaded && (this.footNotes.length === 0 || this.activeNote === null || this.activeNote === undefined);
  }

  enableTitleEdit(note: any) {
    if (note.active) {
      note.input = true;
      this.cdr.detectChanges();
      this.focusAndSelect('.new-note-input');
    }
  }

  finishTitleEdit(note: any) {
    note.input = false;
    this.cdr.detectChanges();
  }

  enableActiveTitleEdit() {
    if (this.activeNote) {
      this.activeNote.titleInput = true;
      this.cdr.detectChanges();
      this.focusAndSelect('.title-input');
    }
  }

  finishActiveTitleEdit() {
    if (this.activeNote) {
      this.activeNote.titleInput = false;
      this.cdr.detectChanges();
    }
  }

  onHOSelect(ho: any) {
    this.selectedHO = ho;
    this.hoSearchTerm = ho.name;
    this.hoDropdownOpen = false;
    this.changeHO();
    this.cdr.detectChanges();
  }

  get filteredHoList(): any[] {
    if (!this.hoSearchTerm) return this.hoList;
    const term = this.hoSearchTerm.toLowerCase();
    return this.hoList.filter((ho: any) => ho.name?.toLowerCase().includes(term));
  }

  onHoSearchFocus() {
    this.hoDropdownOpen = true;
    this.cdr.detectChanges();
  }

  onHoSearchBlur() {
    setTimeout(() => {
      this.hoDropdownOpen = false;
      if (this.selectedHO) {
        this.hoSearchTerm = this.selectedHO.name;
      }
      this.cdr.detectChanges();
    }, 200);
  }

  private activeNoteChanged(): boolean {
    if (!this.activeNoteCopy) return false;
    const nameChanged = this.activeNote.note_name !== this.activeNoteCopy.note_name;
    const dataChanged = this.editor ? this.activeNote.html_data !== this.editor.getData() : false;
    return nameChanged || dataChanged;
  }

  /**
   * Uses the shared confirmation modal, but attached to the CDK overlay instead
   * of this template: the unsaved-changes prompt fires while the workspace is
   * navigating away, when this screen's DOM subtree is already gone, so an
   * inline modal would never become visible. The modal draws its own backdrop
   * and positioning, so the overlay needs no config of its own.
   */
  private showConfirmDialog(
    title: string,
    message: string,
    iconClass: string = 'fa fa-exclamation-triangle txt-color-orangeDark'
  ): Observable<boolean> {
    // A single navigation emits `WorkspaceService.screens` twice (setActivity,
    // then loadTab), so the same prompt gets requested twice. Hand back the open
    // one instead of stacking a second overlay on top of it.
    if (this.pendingConfirm) return this.pendingConfirm;

    const overlayRef = this.overlay.create();
    const modalRef = overlayRef.attach(new ComponentPortal(ConfirmationModalComponent));

    const config = {
      title,
      message,
      iconClass,
      // iconClass already carries its colour class, so drop the config default.
      iconColor: '',
    };
    // Set both ways on purpose: the component merges `config` in ngOnInit, which
    // runs on the change detection pass below, so the input is what survives.
    // The direct assignment covers the case where ngOnInit already ran.
    modalRef.setInput('config', config);
    modalRef.instance.config = { ...modalRef.instance.config, ...config };
    modalRef.changeDetectorRef.detectChanges();

    const result = new Subject<boolean>();
    const pending = result.asObservable();
    this.pendingConfirm = pending;

    const settle = (proceed: boolean) => {
      this.pendingConfirm = undefined;
      overlayRef.dispose();
      result.next(proceed);
      result.complete();
    };
    modalRef.instance.confirmed.subscribe(() => settle(true));
    modalRef.instance.cancelled.subscribe(() => settle(false));
    modalRef.instance.closed.subscribe(() => settle(false));

    return pending;
  }

  private showUnsavedDialog() {
    this.showConfirmDialog(
      'Unsaved Footnote',
      'You have unsaved changes on the footnote, are you sure you want to discard the changes and continue?'
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private focusAndSelect(selector: string) {
    setTimeout(() => {
      const el = document.querySelector(selector) as HTMLInputElement;
      if (el) {
        el.focus();
        el.select();
      }
    }, 0);
  }

  private unexpectedError() {
    this._snackBar.open('Something went wrong! Please try again later.', 'Close', {
      duration: 4000,
      panelClass: ['error-snackbar']
    });
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
